import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectoryCommonHistoryTypeEnum } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/enums/directory-common-history-type.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { TagModule } from 'primeng/tag';
import { forkJoin, Observable } from 'rxjs';
import type { NomenclatureGroupFindInterface } from '@zaoblako/shared/modules/catalog/dtos/nomenclature/nomenclature-group-find.dto';
import {
  NomenclatureGroupFormDto,
  type NomenclatureGroupFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/nomenclature/nomenclature-group-form.dto';
import { NomenclatureGroupService } from '@zaoblako/web/core/services/finance/nomenclature-group.service';
import { NomenclatureTypeNamePipe } from '@zaoblako/web/shared/pipes/nomenclature-type-name.pipe';
import { NomenclatureTypeEnum } from '@zaoblako/shared/modules/catalog/enums/nomenclature-type.enum';
import { NomenclatureGroupSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/nomenclature-group-single-select/nomenclature-group-single-select.component';
import { DirectoryCommonHistoryComponent } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/directory-common-history.component';

@Component({
  standalone: true,
  selector: 'crm-nomenclature-group-form',
  templateUrl: './nomenclature-group-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    InputNumberModule,
    SingleSelectComponent,
    NomenclatureTypeNamePipe,
    NomenclatureGroupSingleSelectComponent,
    SpinnerComponent,
    TagModule,
    FormsModule,
    ButtonComponent,
    DirectoryCommonHistoryComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NomenclatureGroupFormComponent
  extends BaseFormComponent<NomenclatureGroupFormInterface>
  implements OnInit {
  @Input() public readonly index: number;
  public dto = NomenclatureGroupFormDto;
  public nomenclature = <NomenclatureGroupFindInterface>{};

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public types = Object.values(NomenclatureTypeEnum);
  public nomenclatures: NomenclatureGroupFindInterface[] = [];

  public get DirectoryCommonHistoryTypeEnum() {
    return DirectoryCommonHistoryTypeEnum;
  }

  public constructor(
    public readonly formStoreService: FormStoreService,
    public readonly nomenclatureGroupService: NomenclatureGroupService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([this.nomenclatureGroupService.find<NomenclatureGroupFindInterface>()]).subscribe(([nomenclatures]) => {
      this.nomenclatures = nomenclatures;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.nomenclatureGroupService
      .findById<NomenclatureGroupFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, {
        withDeleted: true
      })
      .subscribe({
        next: (nomenclature) => {
          this.nomenclature = nomenclature;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveNomenclature() {
    if (this.formStoreService.getState.forms[this.index]?.entityId) {
      this.formStoreService.setLoading(true, this.index);

      this.nomenclatureGroupService
        .update<NomenclatureGroupFormInterface, NomenclatureGroupFindInterface>(this.nomenclature)
        .subscribe({
          next: (nomenclature) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Номенклатура "${this.nomenclature.name}" обновлена!`);
            this.formStoreService.saveNext(nomenclature, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
    } else {
      this.formStoreService.setLoading(true, this.index);

      this.nomenclatureGroupService.create<NomenclatureGroupFindInterface>(this.nomenclature).subscribe({
        next: (nomenclature) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage('Номенклатура создана!');
          this.formStoreService.saveNext(nomenclature, this.index);
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    }
  }

  public deleteNomenclature() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить номенклатуру "${this.nomenclature.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.nomenclatureGroupService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Номенклатура "${this.nomenclature.name}" удалена!`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public restoreNomenclature() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить номенклатуру "${this.nomenclature.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.nomenclatureGroupService.restoreOne(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.nomenclature = { ...this.nomenclature, deleted: null };
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Номенклатура "${this.nomenclature.name}" восстановлена!`);
            this.formStoreService.saveNext(this.nomenclature, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
